import type { TopicLibraryRow } from '@headline/schemas'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { FilterPills } from '@/components/FilterPills'
import { LibraryTable, type LibraryColumn } from '@/components/LibraryTable'
import { PageHeader } from '@/components/PageHeader'
import { CopyTopicAcrossClientsDialog } from '@/components/topics/CopyTopicAcrossClientsDialog'
import { Button } from '@/components/ui/button'
import { topicLibraryClientsQuery, topicLibraryQuery } from '@/lib/queries'
import { topicOwnerLabel } from '@/lib/topic-overview'
import { cn } from '@/lib/utils'

/**
 * The internal Topics Library — every topic across every client, org-wide. Staff-only
 * (`manage-all:accounts`). Unlike the Template and Coverage libraries, topics have no ownerless
 * "library" scope (each is owned by a client's workspace), so this is a cross-client VIEW plus a
 * cross-account Copy — not an ownership model. See all, filter by status, search, and copy a topic
 * from one client into others; topics are still created and deleted in their own workspace. Table
 * chrome is the shared LibraryTable, styled after coverage lists.
 */
export const Route = createFileRoute('/app/topics/')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(topicLibraryQuery()),
      context.queryClient.ensureQueryData(topicLibraryClientsQuery()),
    ]),
  component: TopicLibraryPage,
})

/** The columns, closed over the copy handler. The name opens the topic's editor in its own
 *  workspace; the status pill mirrors the workspace's live/draft treatment. */
function topicColumns(
  onCopy: (row: TopicLibraryRow) => void,
): Array<LibraryColumn<TopicLibraryRow>> {
  return [
    {
      key: 'name',
      header: 'Topic',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Link
            to="/app/accounts/$accountId/topics/$subaccountId/$topicId/edit"
            params={{ accountId: row.accountId, subaccountId: row.subaccountId, topicId: row.id }}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            {row.name || 'Untitled'}
          </Link>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
              row.status === 'live'
                ? 'border-success/30 bg-success/10 text-success'
                : 'border-warning/40 bg-warning/15 text-warning',
            )}
          >
            <span
              className={cn(
                'size-1.5 rounded-full',
                row.status === 'live' ? 'bg-success' : 'bg-warning',
              )}
            />
            {row.status === 'live' ? 'Live' : 'Draft'}
          </span>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      cellClassName: 'text-muted-foreground',
      cell: (row) => topicOwnerLabel(row),
    },
    {
      key: 'queries',
      header: 'Queries',
      headClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      cell: (row) => row.queryCount,
    },
    {
      key: 'actions',
      header: '',
      cellClassName: 'text-right',
      cell: (row) => (
        <Button variant="ghost" size="sm" onClick={() => onCopy(row)}>
          Copy
        </Button>
      ),
    },
  ]
}

function TopicLibraryPage() {
  const queryClient = useQueryClient()
  const { data: lib } = useSuspenseQuery(topicLibraryQuery())
  const { data: clientsData } = useSuspenseQuery(topicLibraryClientsQuery())
  const [status, setStatus] = useState<'all' | 'live' | 'draft'>('all')
  const [copyTarget, setCopyTarget] = useState<TopicLibraryRow | null>(null)

  const rows = status === 'all' ? lib.topics : lib.topics.filter((topic) => topic.status === status)

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Topics Library"
        description="Every topic across every account. Copy one from one account into another — receiving accounts gets their own editable copy, tags and all."
      />

      <LibraryTable
        rows={rows}
        filtered={status !== 'all'}
        columns={topicColumns(setCopyTarget)}
        rowKey={(topic) => topic.id}
        searchText={(topic) => `${topic.name} ${topicOwnerLabel(topic)}`}
        searchPlaceholder="Search topics…"
        filters={
          <FilterPills
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'live', label: 'Live' },
              { value: 'draft', label: 'Draft' },
            ]}
            value={status}
            onChange={setStatus}
          />
        }
        emptyAll="No topics anywhere yet. Topics are created inside a client's workspace."
        emptyFiltered="No topics match."
      />

      {copyTarget && (
        <CopyTopicAcrossClientsDialog
          topic={copyTarget}
          clients={clientsData.clients}
          onClose={() => setCopyTarget(null)}
          queryClient={queryClient}
        />
      )}
    </div>
  )
}
